/**
 * World — three.js scene, lighting, and the procedural Latium environment.
 *
 * The terrain is the PCG environment (src/render/environment/): chunked LOD
 * heightfield mesh, biome ground painting, instanced Mediterranean
 * vegetation, river/lake water, and a day/night sky rig. The World's
 * groundY/nav height source is the environment's shared Heightfield, so sim
 * and render always agree.
 *
 * Fog of war: the visibility texture is injected into the terrain material
 * via onBeforeCompile (same shader as the old placeholder ground).
 */
import * as THREE from "three";
import { initEnvironment, type Environment } from "./environment/index";

export interface World {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  groundY: (x: number, z: number) => number;
  /**
   * Invisible raycast proxy for ground clicks (placement, orders). Its
   * geometry bakes the PCG heights; the material is never rendered, so the
   * visible terrain comes only from the environment's chunk meshes.
   */
  terrain: THREE.Mesh;
  /** Group holding everything the player can click-select. */
  selectables: THREE.Group;
  /**
   * Sun + hemisphere lights, adopted from the environment sky rig — the
   * single light rig. Driven per-frame by env.setTimeOfDay (not
   * render/daynight.ts, which targets the retired placeholder lighting).
   */
  lights: { hemi: THREE.HemisphereLight; sun: THREE.DirectionalLight };
  /**
   * RGBA DataTexture driven by the VisionSystem (R = team-0 visibility 0..1,
   * G = team-0 explored flag). Sampled by the ground shader for fog of war.
   * Written by FogOfWarRenderer.upload() — never hand-edit the pixels.
   */
  visibilityTex: THREE.DataTexture;
  /** The procedural Latium environment (terrain, water, sky, scatter). */
  env: Environment;
  resize: () => void;
  render: () => void;
}

/**
 * MeshStandardMaterial with the fog-of-war visibility texture injected.
 * Chunk meshes are untransformed at the origin with world-space positions
 * baked into their attributes, so position.xz maps directly onto the
 * playable area (-HALF..HALF) -> visibility texture UVs.
 */
function makeFogAwareGroundMaterial(
  visibilityTex: THREE.DataTexture,
  half: number,
): THREE.MeshStandardMaterial {
  const groundMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 1,
    metalness: 0,
  });
  groundMat.onBeforeCompile = (shader) => {
    shader.uniforms.visibilityMap = { value: visibilityTex };
    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        "#include <common>\nvarying vec2 vVisUv;",
      )
      .replace(
        "#include <begin_vertex>",
        // position is the raw geometry attribute (mesh untransformed at origin).
        // UVs map the playable area (-HALF..HALF) onto the visibility texture.
        `#include <begin_vertex>\nvVisUv = clamp((position.xz + ${half.toFixed(1)}) / ${(half * 2).toFixed(1)}, 0.001, 0.999);`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        "#include <common>\nuniform sampler2D visibilityMap;\nvarying vec2 vVisUv;",
      )
      .replace(
        "#include <color_fragment>",
        `#include <color_fragment>
        {
          vec4 fow = texture2D(visibilityMap, vVisUv);
          // explored flag with a soft transition so there is no hard ring.
          float exploredF = smoothstep(0.45, 0.55, fow.g);
          // Soft visibility falloff: diffuse dims toward the sight edge.
          float visF = smoothstep(0.0, 0.45, fow.r);
          vec3 visibleCol = diffuseColor.rgb * mix(0.55, 1.0, visF);
          vec3 exploredCol =
            mix(diffuseColor.rgb, vec3(dot(diffuseColor.rgb, vec3(0.3333))), 0.75) * 0.35;
          vec3 unexploredCol = vec3(0.012, 0.014, 0.012);
          diffuseColor.rgb =
            mix(unexploredCol, mix(exploredCol, visibleCol, visF), exploredF);
        }`,
      );
  };
  // Keep this program out of the shared cache (uniforms are unique to ground).
  groundMat.customProgramCacheKey = () => "legionnaire-ground-fow";
  return groundMat;
}

export interface WorldOpts {
  /**
   * Playable half-extent in world units (from the skirmish map size).
   * Sizes the environment, the fog-of-war UV mapping, the shadow
   * frustum, and the distance fog. Defaults to the classic 200.
   */
  halfExtent?: number;
  /**
   * Deterministic terrain seed. Defaults to a per-map-size seed so each
   * size has a stable, hand-verified landscape.
   */
  seed?: string;
  /**
   * Vertical exaggeration of the PCG relief. Default 0.3: keeps hills
   * inside the gameplay vertical budget while reading as Latium.
   */
  relief?: number;
}

export function createWorld(container: HTMLElement, opts?: WorldOpts): World {
  const HALF = opts?.halfExtent ?? 200;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0d0f12);

  const camera = new THREE.PerspectiveCamera(
    50,
    container.clientWidth / container.clientHeight,
    0.5,
    1200,
  );

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  container.appendChild(renderer.domElement);

  // --- Fog-of-war visibility texture ---
  // RGBA, VIS_CELLS×VIS_CELLS: R = team-0 visibility 0..1, G = team-0
  // explored flag. Must match VisionSystem.cells in src/sim/vision.ts;
  // FogOfWarRenderer.upload() guards the byte length on copy.
  // Linear filtering smooths the falloff between cells (no hard ring).
  const VIS_CELLS = 128;
  const visibilityTex = new THREE.DataTexture(
    new Uint8Array(VIS_CELLS * VIS_CELLS * 4),
    VIS_CELLS,
    VIS_CELLS,
  );
  visibilityTex.magFilter = THREE.LinearFilter;
  visibilityTex.minFilter = THREE.LinearFilter;
  visibilityTex.generateMipmaps = false;
  visibilityTex.needsUpdate = true;

  // --- Procedural Latium environment (replaces the placeholder terrain) ---
  const env = initEnvironment(scene, {
    seed: opts?.seed ?? `latium-skirmish-${HALF}`,
    size: HALF * 2,
    relief: opts?.relief ?? 0.3,
    terrainMaterial: makeFogAwareGroundMaterial(visibilityTex, HALF),
    shadowExtent: HALF * 1.2,
  });

  // --- Ground-click raycast proxy ---
  // Invisible mesh with the PCG heights baked in. material.visible = false
  // keeps it out of the render list while Mesh.raycast still hits it, so
  // placement/orders raycasts keep working against the real terrain shape.
  const PROXY_SIZE = HALF * 2 + 80;
  const PROXY_SEG = 96;
  const proxyGeo = new THREE.PlaneGeometry(PROXY_SIZE, PROXY_SIZE, PROXY_SEG, PROXY_SEG);
  proxyGeo.rotateX(-Math.PI / 2);
  const pp = proxyGeo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pp.count; i++) {
    pp.setY(i, env.heightAt(pp.getX(i), pp.getZ(i)));
  }
  proxyGeo.computeBoundingSphere();
  const proxy = new THREE.Mesh(proxyGeo, new THREE.MeshBasicMaterial({ visible: false }));
  proxy.name = "terrain";
  scene.add(proxy);

  const selectables = new THREE.Group();
  selectables.name = "selectables";
  scene.add(selectables);

  const resize = () => {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  };
  window.addEventListener("resize", resize);

  return {
    scene,
    camera,
    renderer,
    groundY: env.heightAt,
    terrain: proxy,
    selectables,
    lights: { hemi: env.sky.hemi, sun: env.sky.sun },
    visibilityTex,
    env,
    resize,
    render: () => renderer.render(scene, camera),
  };
}
